import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import type { RootState } from 'app/store';
import type { ISchemaLevelObject } from 'utils/types';
import type { ISqlModel } from '../types/sqlBuilderTypes';

export interface IUseCaseProgramRelationship {
  business_use_case: string;
  program: string;
}

export interface IAstraZenecaState {
  tablesFromStoredMetadata: ISchemaLevelObject[];
  skipSnowflakeConnectionStep: boolean;
  additionalSourceDatabases: string[];
  name: string;
  description: string;
  jiraTicketNo: string;
  dataProductManager: string;
  version: string;
  versionDescription: string;
  schemaDescription: string;
  dataProductCategory: string;
  dataProductType: string;
  containsPatientData: boolean;
  containsPersonalData: boolean;
  isGxpValidated: boolean;
  isSoxControlled: boolean;
  purpose: string;
  specification1: string;
  sourceSystem: string;
  application: string;
  processingFrequency: string;
  gradCode: string;
  lineOfBusinessOwningDP: string;
  dataDomain: string;
  relationships: IUseCaseProgramRelationship[];
  url: string;
  businessTerm: string;
  businessTermDefinition: string;
  isPowerUserMode: boolean;
  models: ISqlModel[] | undefined;
  reviewedByInformationArchitect: boolean;
  hasIncorrectFilenames: boolean;
  securityClassification: string;
}

export const initialState: IAstraZenecaState = {
  tablesFromStoredMetadata: [],
  skipSnowflakeConnectionStep: false,
  additionalSourceDatabases: [],
  name: '',
  description: '',
  jiraTicketNo: '',
  dataProductManager: '',
  version: '',
  versionDescription: '',
  schemaDescription: '',
  dataProductCategory: '',
  dataProductType: '',
  containsPatientData: false,
  containsPersonalData: false,
  isGxpValidated: false,
  isSoxControlled: false,
  purpose: '',
  specification1: '',
  sourceSystem: '',
  application: 'Snowflake (PRD)',
  processingFrequency: '',
  gradCode: 'GRAD - 0000',
  lineOfBusinessOwningDP: '',
  dataDomain: '',
  relationships: [],
  url: '',
  businessTerm: '',
  businessTermDefinition: '',
  isPowerUserMode: true,
  models: undefined,
  reviewedByInformationArchitect: false,
  hasIncorrectFilenames: false,
  securityClassification: '',
};

export const astraZenecaSlice = createSlice({
  name: 'az',
  initialState,
  reducers: {
    setTablesFromStoredMetadata: (state, action: PayloadAction<ISchemaLevelObject[]>) => {
      state.tablesFromStoredMetadata = action.payload;
    },
    setSkipSnowflakeConnectionStep: (state, action: PayloadAction<boolean>) => {
      state.skipSnowflakeConnectionStep = action.payload;
    },
    setAdditionalSourceDatabases: (state, action: PayloadAction<string[]>) => {
      state.additionalSourceDatabases = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    setJiraTicketNo: (state, action: PayloadAction<string>) => {
      state.jiraTicketNo = action.payload;
    },
    setDataProductManager: (state, action: PayloadAction<string>) => {
      state.dataProductManager = action.payload;
    },
    setVersion: (state, action: PayloadAction<string>) => {
      state.version = action.payload;
    },
    setVersionDescription: (state, action: PayloadAction<string>) => {
      state.versionDescription = action.payload;
    },
    setSchemaDescription: (state, action: PayloadAction<string>) => {
      state.schemaDescription = action.payload;
    },
    setDataProductCategory: (state, action: PayloadAction<string>) => {
      state.dataProductCategory = action.payload;
    },
    setDataProductType: (state, action: PayloadAction<string>) => {
      state.dataProductType = action.payload;
    },
    setContainsPatientData: (state, action: PayloadAction<boolean>) => {
      state.containsPatientData = action.payload;
    },
    setContainsPersonalData: (state, action: PayloadAction<boolean>) => {
      state.containsPersonalData = action.payload;
    },
    setIsGxpValidated: (state, action: PayloadAction<boolean>) => {
      state.isGxpValidated = action.payload;
    },
    setIsSoxControlled: (state, action: PayloadAction<boolean>) => {
      state.isSoxControlled = action.payload;
    },
    setPurpose: (state, action: PayloadAction<string>) => {
      state.purpose = action.payload;
    },
    setSpecification1: (state, action: PayloadAction<string>) => {
      state.specification1 = action.payload;
    },
    setSourceSystem: (state, action: PayloadAction<string>) => {
      state.sourceSystem = action.payload;
    },
    setApplication: (state, action: PayloadAction<string>) => {
      state.application = action.payload;
    },
    setProcessingFrequency: (state, action: PayloadAction<string>) => {
      state.processingFrequency = action.payload;
    },
    setGradCode: (state, action: PayloadAction<string>) => {
      state.gradCode = action.payload;
    },
    setLineOfBusinessOwningDP: (state, action: PayloadAction<string>) => {
      state.lineOfBusinessOwningDP = action.payload;
    },
    setDataDomain: (state, action: PayloadAction<string>) => {
      state.dataDomain = action.payload;
    },
    setRelationships: (state, action: PayloadAction<IUseCaseProgramRelationship[]>) => {
      state.relationships = action.payload;
    },
    setUrl: (state, action: PayloadAction<string>) => {
      state.url = action.payload;
    },
    setBusinessTerm: (state, action: PayloadAction<string>) => {
      state.businessTerm = action.payload;
    },
    setBusinessTermDefinition: (state, action: PayloadAction<string>) => {
      state.businessTermDefinition = action.payload;
    },
    setIsPowerUserMode: (state, action: PayloadAction<boolean>) => {
      state.isPowerUserMode = action.payload;
    },
    setModels: (state, action: PayloadAction<ISqlModel[] | undefined>) => {
      state.models = action.payload;
    },
    setReviewedByInformationArchitect: (state, action: PayloadAction<boolean>) => {
      state.reviewedByInformationArchitect = action.payload;
    },
    setHasIncorrectFilenames: (state, action: PayloadAction<boolean>) => {
      state.hasIncorrectFilenames = action.payload;
    },
    setSecurityClassification: (state, action: PayloadAction<string>) => {
      state.securityClassification = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => {
      return initialState;
    });
  },
});

export const {
  setTablesFromStoredMetadata,
  setSkipSnowflakeConnectionStep,
  setAdditionalSourceDatabases,
  setName,
  setDescription,
  setJiraTicketNo,
  setDataProductManager,
  setVersion,
  setVersionDescription,
  setSchemaDescription,
  setDataProductCategory,
  setDataProductType,
  setContainsPatientData,
  setContainsPersonalData,
  setIsGxpValidated,
  setIsSoxControlled,
  setPurpose,
  setSpecification1,
  setSourceSystem,
  setApplication,
  setProcessingFrequency,
  setGradCode,
  setLineOfBusinessOwningDP,
  setDataDomain,
  setRelationships,
  setUrl,
  setBusinessTerm,
  setBusinessTermDefinition,
  setIsPowerUserMode,
  setModels,
  setReviewedByInformationArchitect,
  setHasIncorrectFilenames,
  setSecurityClassification,
} = astraZenecaSlice.actions;

export const selectTablesFromStoredMetadata = (state: RootState): ISchemaLevelObject[] =>
  state.az.tablesFromStoredMetadata;
export const selectSkipSnowflakeConnectionStep = (state: RootState): boolean => state.az.skipSnowflakeConnectionStep;
export const selectAdditionalSourceDatabases = (state: RootState): string[] => state.az.additionalSourceDatabases;
export const selectName = (state: RootState): string => state.az.name;
export const selectDescription = (state: RootState): string => state.az.description;
export const selectJiraTicketNo = (state: RootState): string => state.az.jiraTicketNo;
export const selectDataProductManager = (state: RootState): string => state.az.dataProductManager;
export const selectVersion = (state: RootState): string => state.az.version;
export const selectVersionDescription = (state: RootState): string => state.az.versionDescription;
export const selectSchemaDescription = (state: RootState): string => state.az.schemaDescription;
export const selectDataProductCategory = (state: RootState): string => state.az.dataProductCategory;
export const selectDataProductType = (state: RootState): string => state.az.dataProductType;
export const selectContainsPatientData = (state: RootState): boolean => state.az.containsPatientData;
export const selectContainsPersonalData = (state: RootState): boolean => state.az.containsPersonalData;
export const selectIsGxpValidated = (state: RootState): boolean => state.az.isGxpValidated;
export const selectIsSoxControlled = (state: RootState): boolean => state.az.isSoxControlled;
export const selectPurpose = (state: RootState): string => state.az.purpose;
export const selectSpecification1 = (state: RootState): string => state.az.specification1;
export const selectSourceSystem = (state: RootState): string => state.az.sourceSystem;
export const selectApplication = (state: RootState): string => state.az.application;
export const selectProcessingFrequency = (state: RootState): string => state.az.processingFrequency;
export const selectGradCode = (state: RootState): string => state.az.gradCode;
export const selectLineOfBusinessOwningDP = (state: RootState): string => state.az.lineOfBusinessOwningDP;
export const selectDataDomain = (state: RootState): string => state.az.dataDomain;
export const selectRelationships = (state: RootState): IUseCaseProgramRelationship[] => state.az.relationships;
export const selectUrl = (state: RootState): string => state.az.url;
export const selectBusinessTerm = (state: RootState): string => state.az.businessTerm;
export const selectBusinessTermDefinition = (state: RootState): string => state.az.businessTermDefinition;
export const selectIsPowerUserMode = (state: RootState): boolean => state.az.isPowerUserMode;
export const selectModels = (state: RootState): ISqlModel[] | undefined => state.az.models;
export const selectReviewedByInformationArchitect = (state: RootState): boolean =>
  state.az.reviewedByInformationArchitect;
export const selectHasIncorrectFilenames = (state: RootState): boolean => state.az.hasIncorrectFilenames;
export const selectSecurityClassification = (state: RootState): string => state.az.securityClassification;

/**
 * Derived selector: joins all business_use_case values from relationships into
 * a comma-separated string. Used by components that still read the flat value
 * (e.g. display, API serialisation before Phase 6 migration).
 */
export const selectDerivedBusinessUseCase = (state: RootState): string =>
  state.az.relationships.map((r) => r.business_use_case).join(', ');

/**
 * Derived selector: joins all program values from relationships into a
 * comma-separated string. Used by components that still read the flat value
 * (e.g. display, API serialisation before Phase 6 migration).
 */
export const selectDerivedProgram = (state: RootState): string =>
  state.az.relationships.map((r) => r.program).join(', ');

export default astraZenecaSlice.reducer;
